import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Body, Chip, Heading, MaterialIcons } from '@iclinic/design-system';

import { monetaryFormatter } from 'shared/utils/formatters';
import {
  getSelectedPlan,
  getSelectedAddons,
  getPhysicianByAddon,
  getPhysicians,
  getCouponCode,
  getAppliedVoucher,
  getVoucherErrorMessage,
  getIsVoucher,
  getIsVoucherOrCouponApplied,
} from '../../state/selectors';
import { getSummaryData } from '../../utils/summary';
import * as S from './PaymentSummary.styles';
import {
  applyVoucher,
  applyCouponSuccess,
  removeVoucher,
  setAppliedVoucher,
  setIsVoucherOrCouponApplied,
} from 'features/checkout/state/reducer';
import VoucherInput from './VoucherInput';
import {
  saveIsVoucherApplied,
  loadIsVoucherApplied,
} from 'features/checkout/utils';

interface PaymentSummaryProps {
  isAnnual: boolean;
  showSinglePrice?: boolean;
}

const { WarningAmberOutlined } = MaterialIcons;

const PaymentSummary = ({
  isAnnual,
  showSinglePrice = false,
}: PaymentSummaryProps) => {
  const dispatch = useDispatch();
  const selectedAddons = useSelector(getSelectedAddons);
  const selectedPlan = useSelector(getSelectedPlan);
  const physicians = useSelector(getPhysicians);
  const physicianByAddon = useSelector(getPhysicianByAddon);
  const couponData = useSelector(getCouponCode);
  const appliedVoucher = useSelector(getAppliedVoucher);
  const voucherErrorMessage = useSelector(getVoucherErrorMessage);
  const isVoucher = useSelector(getIsVoucher);
  const isVoucherOrCouponApplied = useSelector(getIsVoucherOrCouponApplied);

  const [codeValue, setCodeValue] = useState('');
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);

  const {
    planValue,
    planTextToAppend,
    discountValue,
    percentageValue,
    totalValue,
    addons,
  } = getSummaryData({
    isAnnual,
    addons: selectedAddons,
    plan: selectedPlan,
    physicianByAddon,
    physicians,
    showSinglePrice,
    coupon: couponData,
  });

  const handleApplyVoucher = () => {
    if (codeValue.trim()) {
      saveIsVoucherApplied('true');
      dispatch(setAppliedVoucher('true'));
      dispatch(applyVoucher({ voucher: codeValue }));
    }
  };

  const handleChange = (value: string) => {
    setCodeValue(value);
  };

  const handleRemoveVoucher = () => {
    setShowRemoveAlert(true);
  };

  const confirmRemoveVoucher = () => {
    dispatch(removeVoucher({ voucher: codeValue }));
    setCodeValue('');
    setShowRemoveAlert(false);
  };

  useEffect(() => {
    if (couponData?.code !== null) {
      const savedVoucher = loadIsVoucherApplied();
      if (savedVoucher) {
        dispatch(setAppliedVoucher(savedVoucher));
      }
    }

    if (couponData?.code) {
      dispatch(setIsVoucherOrCouponApplied(true));
      setCodeValue(couponData.code);
      dispatch(applyCouponSuccess(couponData.code));
    }
  }, [couponData, dispatch]);

  useEffect(() => {
    if (!couponData?.code) {
      setCodeValue(appliedVoucher ?? '');
    }
  }, [appliedVoucher, couponData]);

  return (
    <S.Card>
      <Heading variant="sm" gutterBottom>
        Resumo de sua assinatura
      </Heading>

      <S.Divider />

      <S.ProductSelectedWrapper>
        <div>
          <S.DescriptionTitle variant="xs">
            {selectedPlan
              ? `Plano ${selectedPlan.name}`
              : 'Nenhum plano selecionado'}
          </S.DescriptionTitle>
          <Body variant="xs">
            Assinatura {isAnnual ? 'anual' : 'mensal'} {planTextToAppend}
          </Body>
        </div>
        <Heading variant="xs">R$ {monetaryFormatter(planValue)} </Heading>
      </S.ProductSelectedWrapper>

      <S.VoucherWrapper>
        <VoucherInput
          value={codeValue}
          isApplied={isVoucherOrCouponApplied}
          error={voucherErrorMessage}
          onApply={handleApplyVoucher}
          onRemove={handleRemoveVoucher}
          onChange={handleChange}
          isVoucher={isVoucher}
        />
      </S.VoucherWrapper>

      {showRemoveAlert && (
        <S.WarningRemoveVoucher>
          <S.WarningContent>
            <WarningAmberOutlined />
            <S.Body variant="xs">
              Tem certeza que deseja remover o desconto?
            </S.Body>
          </S.WarningContent>
          <S.WarningButton>
            <S.Button
              variant="text"
              size="small"
              onClick={() => setShowRemoveAlert(false)}
            >
              Cancelar
            </S.Button>
            <S.Button
              variant="text"
              size="small"
              onClick={confirmRemoveVoucher}
            >
              Remover
            </S.Button>
          </S.WarningButton>
        </S.WarningRemoveVoucher>
      )}

      {discountValue &&
        codeValue &&
        isVoucherOrCouponApplied &&
        !voucherErrorMessage && (
          <S.ProductSelectedWrapper>
            <Chip
              label={`Cupom de ${percentageValue || ''}% aplicado`}
              severity="success"
              variant="default"
              size="small"
            />
            <Heading variant="xs">
              - R$ {monetaryFormatter(discountValue)}
            </Heading>
          </S.ProductSelectedWrapper>
        )}
      {!discountValue &&
        codeValue &&
        appliedVoucher &&
        !voucherErrorMessage && (
          <S.ProductSelectedWrapper>
            <Chip
              label="Cupom indisponível para este plano ou frequência"
              severity="error"
              variant="default"
              size="small"
            />
          </S.ProductSelectedWrapper>
        )}

      {addons.map((addon) => (
        <S.ProductSelectedWrapper key={addon.slug}>
          <div>
            <S.DescriptionTitle variant="xs">
              Pacote de {addon.name}
            </S.DescriptionTitle>
            <Body variant="xs">
              Assinatura {isAnnual ? 'anual' : 'mensal'} {addon.textToAppend}
            </Body>
          </div>
          <S.PriceContainer variant="xs">
            + R$ {monetaryFormatter(addon.price)}
          </S.PriceContainer>
        </S.ProductSelectedWrapper>
      ))}

      <S.Divider />

      <S.TotalWrapper>
        <S.DescriptionTitle variant="sm">Total</S.DescriptionTitle>
        <S.TotalAmount>
          <Heading variant="sm">R$ {monetaryFormatter(totalValue)}</Heading>
          <Body variant="xs"> por {isAnnual ? 'ano' : 'mês'}</Body>
        </S.TotalAmount>
      </S.TotalWrapper>
    </S.Card>
  );
};

export default PaymentSummary;
